import { fn } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismTooltip } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { ParityStrategyIssue } from 'lumen/models';

interface ParityTablesCellsTdPushIssueSignature {
  Args: {
    cellData: ParityStrategyIssue;
  };
}

export default class ParityTablesCellsTdPushIssue extends Component<ParityTablesCellsTdPushIssueSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isLoading = false;

  pushIssueToParityManagerTask = task(
    { drop: true },
    async (issue): Promise<void> => {
      if (this.isLoading) {
        return;
      }

      this.usageAnalytics.track('Hotel Issue Pushed', {
        eventTrigger: 'hotel-issue-push-button',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        hotelId: issue.subscriptionId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        channel: issue.channel,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        type: issue.promotionType,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        los: issue.los,
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      issue.pushedToParityManager = true;
      this.isLoading = true;

      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        await issue.save();

        this.toast.success(
          this.intl.t('parity_strategy.toast.pushed_to_parity_manager'),
          { timeout: TOAST_TIMEOUT_LONG },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        issue.rollbackAttributes();

        this.toast.alert(this.intl.t('general.error_title'), {
          timeout: TOAST_TIMEOUT_LONG,
        });

        this.errorTracking.captureException(error);
      } finally {
        this.isLoading = false;
      }
    },
  );

  revertPushIssueToParityManagerTask = task(
    { drop: true },
    async (issue): Promise<void> => {
      if (this.isLoading) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      issue.pushedToParityManager = false;
      this.isLoading = true;
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        await issue.save();
        this.toast.success(
          this.intl.t('parity_strategy.toast.reverted_push_to_parity_manager'),
          { timeout: TOAST_TIMEOUT_LONG },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        issue.rollbackAttributes();

        this.toast.alert(this.intl.t('general.error_title'), {
          timeout: TOAST_TIMEOUT_LONG,
        });

        this.errorTracking.captureException(error);
      } finally {
        this.isLoading = false;
      }
    },
  );

  <template>
    {{#if @cellData.isScreenshotSupported}}
      {{#if @cellData.pushedToParityManager}}
        <PrismButton
          @buttonType="secondary"
          @isLoading={{this.isLoading}}
          @label={{t "general.revert"}}
          @onClick={{fn
            this.revertPushIssueToParityManagerTask.perform
            @cellData
          }}
          @size="small"
          class="w-full"
        />
      {{else}}
        <PrismButton
          @buttonType="secondary"
          @iconAfter="arrow-right"
          @isDisabled={{not @cellData.canBePushedToParityManager}}
          @isLoading={{this.isLoading}}
          @label={{t "general.push"}}
          @onClick={{fn this.pushIssueToParityManagerTask.perform @cellData}}
          @size="small"
          class="w-full"
        />
        {{#if @cellData.isNonAllowedOta}}
          <PrismTooltip
            @side="top"
            @text={{t
              "parity_strategy.tooltip.push_to_parity_manager.non_allowed_ota"
            }}
          />
        {{else if (not @cellData.canBePushedToParityManager)}}
          <PrismTooltip
            @side="top"
            @text={{t
              "parity_strategy.tooltip.push_to_parity_manager.missing_screenshot"
            }}
          />
        {{else}}
          <PrismTooltip
            @side="top"
            @text={{t "parity_strategy.tooltip.push_to_parity_manager.message"}}
          />
        {{/if}}
      {{/if}}
    {{/if}}
  </template>
}
